import type { InstrumentSetupResponse } from "../types/instrumentSetup";
import type { AvailableInstrumentRow } from "../types/tradeManagementReport";

/** Row with authoritative BE readiness fields (instrument-setup, resolve, available-instruments). */
export type InstrumentMarketDataRow = AvailableInstrumentRow & {
  setup_complete?: boolean;
  blocking_reason?: string | null;
  requires_ohlc_attention?: boolean;
  ohlc_ready?: boolean;
  status?: "pending" | "resolved";
};

export type InstrumentSetupAccount = Pick<
  InstrumentSetupResponse,
  | "requires_instrument_confirmation"
  | "blocking_symbol_count"
  | "setup_complete_count"
  | "total_symbols"
  | "resolved_count"
  | "pending_count"
  | "pending"
  | "resolved"
>;

/** Layer A — user must finish instrument setup wizard. */
export function instrumentNeedsSetup(
  row: InstrumentMarketDataRow | null | undefined
): boolean {
  if (!row || row.setup_skipped) return false;
  if (typeof row.setup_complete === "boolean") {
    return !row.setup_complete;
  }
  if (row.blocking_reason) return true;
  return false;
}

/** Layer B — charts blocked for this symbol (setup incomplete or lane unavailable). */
export function instrumentChartsBlocked(
  row: InstrumentMarketDataRow | null | undefined
): boolean {
  if (!row || row.setup_skipped) return false;
  if (instrumentNeedsSetup(row)) return true;
  if (row.chart_available === false) return true;
  if (row.chart_available === true) return false;
  if (row.ohlc_ready === false) return true;
  return false;
}

/** Account-wide setup gate — any symbol still needs wizard confirmation. */
export function accountNeedsInstrumentSetup(
  scan: InstrumentSetupAccount | null | undefined
): boolean {
  if (!scan) return false;
  if (scan.requires_instrument_confirmation === true) return true;
  if (typeof scan.blocking_symbol_count === "number") {
    return scan.blocking_symbol_count > 0;
  }
  const rows = [...(scan.resolved ?? []), ...(scan.pending ?? [])];
  return rows.some((row) => instrumentNeedsSetup(row));
}

/** Setup wizard progress bar. */
export function setupProgress(scan: InstrumentSetupAccount | null | undefined): {
  done: number;
  total: number;
  needsConfirmation: number;
} {
  if (!scan) return { done: 0, total: 0, needsConfirmation: 0 };
  const total = Number(scan.total_symbols ?? 0);
  const done = Number(scan.setup_complete_count ?? scan.resolved_count ?? 0);
  const needsConfirmation = Number(scan.blocking_symbol_count ?? scan.pending_count ?? 0);
  if (total > 0) {
    return { done, total, needsConfirmation };
  }
  const rows = [...(scan.resolved ?? []), ...(scan.pending ?? [])];
  let configured = 0;
  let pending = 0;
  for (const row of rows) {
    if (instrumentNeedsSetup(row)) pending += 1;
    else configured += 1;
  }
  return {
    done: configured,
    total: rows.length,
    needsConfirmation: pending,
  };
}

export function formatInstrumentSetupProgressDescription(
  scan: InstrumentSetupAccount | null | undefined,
  fallback = "Select a symbol to configure market data."
): string {
  if (!scan) return fallback;
  const { done, total, needsConfirmation } = setupProgress(scan);
  if (needsConfirmation <= 0) {
    return `Progress: ${done} / ${total} configured.`;
  }
  return `Progress: ${done} / ${total} configured · ${needsConfirmation} need confirmation.`;
}

export function findInstrumentMarketDataRow(
  scan: InstrumentSetupAccount | null | undefined,
  symbol: string | null | undefined
): InstrumentMarketDataRow | undefined {
  const needle = symbol?.trim().toUpperCase();
  if (!needle || !scan) return undefined;
  const rows = [...(scan.resolved ?? []), ...(scan.pending ?? [])];
  return rows.find((row) => row.symbol.trim().toUpperCase() === needle);
}

/**
 * Row for OHLC gating — match symbol when known, else single-symbol account fallback.
 */
export function resolveInstrumentRowForScan(
  scan: InstrumentSetupAccount | null | undefined,
  symbol?: string | null
): InstrumentMarketDataRow | undefined {
  const needle = symbol?.trim();
  if (needle) {
    const found = findInstrumentMarketDataRow(scan, needle);
    if (found) return found;
  }
  const rows = [...(scan?.resolved ?? []), ...(scan?.pending ?? [])];
  if (rows.length === 1) return rows[0];
  return undefined;
}

/** Row still needs wizard confirmation (setup UI labels). */
export function instrumentSetupRowNeedsConfirmation(
  row: InstrumentMarketDataRow | null | undefined
): boolean {
  return instrumentNeedsSetup(row);
}
